import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Button, Chip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import {
  DEFAULT_DEMO_SCENARIO_ID,
  demoScenarioGroups,
  getDemoScenario,
} from '../../config/demoScenarios';
import { isDesignMode } from '../../config/designMode';
import {
  runDesignDeployAll,
  runDesignDestroyAll,
} from '../Canvas/utils/designHarness';
import FlowCanvas from '../Canvas/components/FlowCanvas';
import LoadingScreen from '../Canvas/components/LoadingScreen';
import ModalsContainer from '../Canvas/components/ModalsContainer';
import { useFlowDiagram } from '../Canvas/hooks/useFlowDiagram';
import './demo.css';

const DEMO_STEP_MS = 400;
const HOLD_MS = 900;

const PHASE_LABEL = {
  idle: 'idle',
  provisioning: 'provisioning',
  provisioned: 'provisioned',
  deprovisioning: 'deprovisioning',
};

function stamp() {
  return new Date().toLocaleTimeString([], {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const Demo = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [generation, setGeneration] = useState(0);
  const scenario = getDemoScenario(projectId) || getDemoScenario(DEFAULT_DEMO_SCENARIO_ID);
  const groups = useMemo(() => demoScenarioGroups(), []);

  useEffect(() => {
    if (!getDemoScenario(projectId)) {
      navigate(`/demo/${DEFAULT_DEMO_SCENARIO_ID}`, { replace: true });
    }
  }, [projectId, navigate]);

  return (
    <Box className="demo-shell">
      <aside className="demo-rail">
        <p className="demo-kicker">Walk</p>
        <h1 className="demo-title">Provision order</h1>
        <p className="demo-lede">
          Each layer runs together. Apply walks sources first; destroy
          walks dependents first. Same order the bash harness uses.
        </p>
        {groups.map((group) => (
          <section key={group.id} className="demo-group">
            <h2 className="demo-group__title">{group.title}</h2>
            <p className="demo-group__blurb">{group.blurb}</p>
            <div className="demo-group__list">
              {group.items.map((item) => {
                const active = item.id === scenario.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`demo-scenario${active ? ' is-active' : ''}`}
                    onClick={() => navigate(`/demo/${item.id}`)}
                  >
                    <span className="demo-scenario__name">{item.name}</span>
                    <span className="demo-scenario__meta">
                      {(item.patterns || []).slice(0, 3).join(' · ')}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </aside>
      <DemoStage
        key={`${scenario.id}-${generation}`}
        scenario={scenario}
        onReset={() => setGeneration((n) => n + 1)}
      />
    </Box>
  );
};

const DemoStage = ({ scenario, onReset }) => {
  const navigate = useNavigate();
  const design = isDesignMode();
  const [phase, setPhase] = useState('idle');
  const [lines, setLines] = useState([]);
  const tapeRef = useRef(null);
  const abortRef = useRef(null);
  const autoStarted = useRef(false);
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onEdgesDelete,
    onConnect,
    onConnectStart,
    onConnectEnd,
    onConnectCheck,
    onInit,
    onDrop,
    onDragOver,
    isLoading,
    error,
    handleSubmitForm,
    modalState,
    setModalState,
    handleDeploy,
    setNodes,
    reactFlowWrapper,
  } = useFlowDiagram();

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
  }, [nodes, edges]);

  useEffect(() => {
    if (tapeRef.current) {
      tapeRef.current.scrollTop = tapeRef.current.scrollHeight;
    }
  }, [lines]);

  const append = useCallback((text, extra = {}) => {
    setLines((prev) => [
      ...prev,
      { id: `${Date.now()}-${prev.length}`, at: stamp(), text, ...extra },
    ]);
  }, []);

  const stopWalk = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  useEffect(() => () => stopWalk(), [stopWalk]);

  const runWalk = useCallback(
    async (kind) => {
      if (!design) return;
      stopWalk();
      const ac = new AbortController();
      abortRef.current = ac;
      const onStep = (step) => {
        const verb =
          step.status === 'DEPLOYING'
            ? 'apply'
            : step.status === 'DESTROYING'
              ? 'destroy'
              : step.status === 'DEPLOYED'
                ? 'ready'
                : 'cleared';
        append(`${verb}  ${step.label}`, { live: verb === 'apply' || verb === 'destroy' });
      };
      try {
        if (kind === 'provision' || kind === 'cycle') {
          setPhase('provisioning');
          append('provision — sources first, siblings together');
          await runDesignDeployAll({
            nodes: nodesRef.current,
            edges: edgesRef.current,
            setNodes,
            stepMs: DEMO_STEP_MS,
            onStep,
            signal: ac.signal,
          });
          if (ac.signal.aborted) return;
          setPhase('provisioned');
          append('graph provisioned');
        }
        if (kind === 'cycle') {
          await new Promise((resolve, reject) => {
            const timer = setTimeout(resolve, HOLD_MS);
            ac.signal.addEventListener(
              'abort',
              () => {
                clearTimeout(timer);
                reject(new DOMException('aborted', 'AbortError'));
              },
              { once: true }
            );
          });
        }
        if (kind === 'deprovision' || kind === 'cycle') {
          if (ac.signal.aborted) return;
          setPhase('deprovisioning');
          append('deprovision — dependents first, siblings together');
          await runDesignDestroyAll({
            nodes: nodesRef.current,
            edges: edgesRef.current,
            setNodes,
            stepMs: DEMO_STEP_MS,
            onStep,
            signal: ac.signal,
          });
          if (ac.signal.aborted) return;
          setPhase('idle');
          append('graph idle');
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
        append(`failed  ${err.message || err}`);
        setPhase('idle');
      }
    },
    [append, design, setNodes, stopWalk]
  );

  useEffect(() => {
    if (!design || isLoading || !nodes.length || autoStarted.current) return;
    if (!scenario.watchOnLoad) return;
    autoStarted.current = true;
    const timer = setTimeout(() => {
      runWalk('cycle');
    }, 650);
    return () => clearTimeout(timer);
  }, [design, isLoading, nodes.length, runWalk, scenario.watchOnLoad]);

  const deployed = nodes.filter(
    (n) => n.data?.deploy_status === 'DEPLOYED'
  ).length;

  const flowCanvasProps = useMemo(
    () => ({
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      onEdgesDelete,
      onConnect,
      onConnectStart,
      onConnectEnd,
      onConnectCheck,
      onInit,
      onDrop,
      onDragOver,
      reactFlowWrapper,
    }),
    [
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      onEdgesDelete,
      onConnect,
      onConnectStart,
      onConnectEnd,
      onConnectCheck,
      onInit,
      onDrop,
      onDragOver,
      reactFlowWrapper,
    ]
  );

  const busy = phase === 'provisioning' || phase === 'deprovisioning';

  if (isLoading) return <LoadingScreen />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <Box className="demo-stage">
      <header className="demo-bar">
        <div className="demo-bar__copy">
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
            {scenario.name}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.4 }}>
            {(scenario.patterns || []).map((p) => (
              <Chip key={p} size="small" label={p} />
            ))}
            <Chip
              size="small"
              label={PHASE_LABEL[phase]}
              color={busy ? 'secondary' : 'default'}
            />
            <Chip size="small" label={`${deployed}/${nodes.length} up`} />
          </Box>
        </div>
        <div className="demo-bar__actions">
          <Button
            size="small"
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={!design || busy}
            onClick={() => runWalk('provision')}
          >
            Provision
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<StopIcon />}
            disabled={!design || busy}
            onClick={() => runWalk('deprovision')}
          >
            Deprovision
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            disabled={!design || busy}
            onClick={() => runWalk('cycle')}
          >
            Watch cycle
          </Button>
          <Button
            size="small"
            startIcon={<ReplayIcon />}
            disabled={busy}
            onClick={onReset}
          >
            Reset
          </Button>
          <Button
            size="small"
            startIcon={<AccountTreeIcon />}
            onClick={() => navigate(`/canvas/${scenario.id}`)}
          >
            Edit graph
          </Button>
        </div>
      </header>
      {!design && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
          Run `npm run design` to play the walk. The graph still loads.
        </Typography>
      )}
      <div className="demo-body">
        <div className="demo-graph">
          <ReactFlowProvider>
            <FlowCanvas {...flowCanvasProps} />
            <ModalsContainer
              modalState={modalState}
              setModalState={setModalState}
              onSubmitForm={handleSubmitForm}
              onDeploy={handleDeploy}
              nodes={nodes}
            />
          </ReactFlowProvider>
        </div>
        <aside className="demo-tape" ref={tapeRef} aria-live="polite">
          <p className="demo-tape__head">Run tape</p>
          {lines.length === 0 && (
            <p className="demo-tape__empty">Waiting to walk the DAG…</p>
          )}
          {lines.map((line, i) => (
            <div
              key={line.id}
              className={`demo-tape__line${i === lines.length - 1 ? ' is-live' : ''}`}
            >
              <span className="demo-tape__at">{line.at}</span>
              <span>{line.text}</span>
            </div>
          ))}
        </aside>
      </div>
    </Box>
  );
};

export default Demo;
